import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Chip,
  Box,
  LinearProgress,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import {
  EmojiEvents,
  TrendingUp,
  TrendingDown,
  Remove
} from '@mui/icons-material';

const FriendLeaderboard = ({ leaderboard, period, onPeriodChange, loading }) => {
  const getRankIcon = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return rank;
  };

  const getChangeIcon = (change) => {
    if (!change || change === '0') return <Remove />;
    
    const num = parseInt(change.replace('+', ''));
    if (num > 0) return <TrendingUp color="success" />;
    if (num < 0) return <TrendingDown color="error" />;
    return <Remove />;
  };

  const getChangeColor = (change) => {
    if (!change || change === '0') return 'default';
    
    const num = parseInt(change.replace('+', ''));
    if (num > 0) return 'success';
    if (num < 0) return 'error';
    return 'default';
  };

  const formatSteps = (steps) => {
    if (steps >= 1000000) {
      return `${(steps / 1000000).toFixed(1)}M`;
    }
    if (steps >= 1000) {
      return `${(steps / 1000).toFixed(0)}K`;
    }
    return steps?.toLocaleString() || '0';
  };

  const getProgressPercentage = (steps, maxSteps) => {
    if (!maxSteps || maxSteps === 0) return 0;
    return Math.min((steps / maxSteps) * 100, 100);
  };

  const maxSteps = leaderboard?.length > 0 ? Math.max(...leaderboard.map(item => item.steps)) : 0;

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" display="flex" alignItems="center" gap={1}>
            <EmojiEvents />
            Friend Leaderboard
          </Typography>
          
          {onPeriodChange && (
            <ToggleButtonGroup
              value={period}
              exclusive
              onChange={(e, newPeriod) => newPeriod && onPeriodChange(newPeriod)}
              size="small"
            >
              <ToggleButton value="daily">Daily</ToggleButton>
              <ToggleButton value="weekly">Weekly</ToggleButton>
              <ToggleButton value="monthly">Monthly</ToggleButton>
            </ToggleButtonGroup>
          )}
        </Box>

        {!leaderboard || leaderboard.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography variant="body2" color="text.secondary">
              No leaderboard data available
            </Typography>
          </Box>
        ) : (
          <List disablePadding>
            {leaderboard.map((item, index) => {
              const isCurrentUser = item.user.id === 'current' || item.user.name === 'You';
              
              return (
                <ListItem 
                  key={item.user.id || index}
                  sx={{
                    bgcolor: isCurrentUser ? 'primary.50' : 'transparent',
                    borderRadius: 1,
                    mb: 1,
                    border: isCurrentUser ? '1px solid' : 'none',
                    borderColor: isCurrentUser ? 'primary.main' : 'transparent'
                  }}
                >
                  <ListItemAvatar>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Avatar 
                        src={item.user.avatar}
                        sx={{ 
                          bgcolor: item.rank <= 3 ? 'primary.main' : 'grey.400',
                          fontWeight: 'bold',
                          width: 40,
                          height: 40
                        }}
                      >
                        {typeof getRankIcon(item.rank) === 'string' ? 
                          getRankIcon(item.rank) : 
                          item.rank
                        }
                      </Avatar>
                    </Box>
                  </ListItemAvatar>
                  
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography 
                          variant="body1" 
                          fontWeight={isCurrentUser ? 'bold' : 'medium'}
                        >
                          {item.user.name}
                        </Typography>
                        {isCurrentUser && (
                          <Chip label="You" size="small" color="primary" />
                        )}
                        {item.change && (
                          <Chip 
                            icon={getChangeIcon(item.change)}
                            label={item.change}
                            size="small"
                            color={getChangeColor(item.change)}
                            variant="outlined"
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                          <Typography variant="body2" color="text.secondary">
                            {formatSteps(item.steps)} steps
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Rank #{item.rank}
                          </Typography>
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={getProgressPercentage(item.steps, maxSteps)}
                          sx={{ 
                            height: 6, 
                            borderRadius: 3,
                            bgcolor: 'grey.200',
                            '& .MuiLinearProgress-bar': {
                              bgcolor: item.rank <= 3 ? 'primary.main' : 'grey.400'
                            }
                          }}
                        />
                      </Box>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        )}

        {leaderboard && leaderboard.length > 0 && (
          <Box mt={2} textAlign="center">
            <Typography variant="caption" color="text.secondary">
              {period} leaderboard • {leaderboard.length} participants
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default FriendLeaderboard;